import { apiFetch } from './client.js'

function normalizeElement(element) {
  if (!element || typeof element !== 'object') {
    return null
  }

  return {
    id: element.id,
    applicationId: element.application_id || element.applicationId,
    groupConfigId: element.group_config_id || element.groupConfigId || null,
    name: element.name || '',
    elementType: element.element_type || element.elementType || 'advertising',
    description: element.description || '',
    jsonConfig: element.json_config ?? element.jsonConfig ?? null,
    rawJsonConfig: element.raw_json_config || element.rawJsonConfig || '',
    text: element.text || '',
    version: Number(element.version || 1),
    status: String(element.status || 'draft').trim().toLowerCase(),
    publishedAt: element.published_at || element.publishedAt || null,
    archivedAt: element.archived_at || element.archivedAt || null,
    createdAt: element.created_at || element.createdAt || null,
    updatedAt: element.updated_at || element.updatedAt || null,
  }
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function extractItem(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.item) return payload.item
    if (payload.data && !Array.isArray(payload.data)) return payload.data
  }
  return payload
}

function buildElementCreatePayload(values) {
  const payload = {
    name: String(values?.name || '').trim(),
    element_type: String(values?.elementType || 'advertising').trim(),
    description: String(values?.description || '').trim(),
    raw_json_config: String(values?.rawJsonConfig ?? values?.jsonConfig ?? '').trim(),
    text: String(values?.text || ''),
    version: Number(values?.version || 1) || 1,
    status: String(values?.status || 'draft').trim(),
  }
  const groupConfigId = values?.groupConfigId != null && values?.groupConfigId !== '' ? Number(values.groupConfigId) : null
  if (groupConfigId != null) payload.group_config_id = groupConfigId
  return payload
}

function hasOwn(source, key) {
  return Object.prototype.hasOwnProperty.call(source, key)
}

function buildElementUpdatePayload(values) {
  const payload = {}

  if (hasOwn(values, 'name')) payload.name = String(values.name || '').trim()
  if (hasOwn(values, 'elementType')) payload.element_type = String(values.elementType || '').trim()
  if (hasOwn(values, 'description')) payload.description = String(values.description || '').trim()
  if (hasOwn(values, 'rawJsonConfig') || hasOwn(values, 'jsonConfig')) {
    payload.raw_json_config = String(values.rawJsonConfig ?? values.jsonConfig ?? '').trim()
  }
  if (hasOwn(values, 'text')) payload.text = String(values.text || '')
  if (hasOwn(values, 'version')) payload.version = Number(values.version || 1) || 1
  if (hasOwn(values, 'status')) payload.status = String(values.status || '').trim()
  if (hasOwn(values, 'groupConfigId')) {
    const v = values.groupConfigId
    payload.group_config_id = v != null && v !== '' ? Number(v) : null
  }

  return payload
}

export async function listElements(applicationId) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/elements`)
  return extractList(payload).map(normalizeElement).filter(Boolean)
}

export async function getElement(applicationId, elementId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/elements/${encodeURIComponent(elementId)}`,
  )
  return normalizeElement(extractItem(payload))
}

export async function createElement(applicationId, values) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/elements`, {
    method: 'POST',
    body: buildElementCreatePayload(values),
  })
  return normalizeElement(extractItem(payload))
}

export async function copyElement(applicationId, elementId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/elements/${encodeURIComponent(elementId)}/copy`,
    { method: 'POST' },
  )
  return normalizeElement(extractItem(payload))
}

export async function updateElement(applicationId, elementId, values) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/elements/${encodeURIComponent(elementId)}`,
    {
      method: 'PATCH',
      body: buildElementUpdatePayload(values),
    },
  )
  return normalizeElement(extractItem(payload))
}

export async function publishElement(applicationId, elementId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/elements/${encodeURIComponent(elementId)}/publish`,
    {
      method: 'POST',
    },
  )
  return normalizeElement(extractItem(payload))
}

export async function deleteElement(applicationId, elementId) {
  await apiFetch(`/apps/${encodeURIComponent(applicationId)}/elements/${encodeURIComponent(elementId)}`, {
    method: 'DELETE',
  })
}
